import React, { useCallback, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useHistory } from 'react-router-dom'
import { Checkbox, Dropdown, Icon, Menu, Popconfirm, Tooltip } from 'antd'
import PropTypes from 'prop-types'

import { Button } from '../../../../core/components/styled'
import { THEME } from '../../../../core/constants'

import { ADMIN, TEAM_LEADER } from '../../../../common/constants'
import { useIsLoggedInUser, useUserHasRoles } from '../../../../common/hooks'
import { getCachedFilterForPage } from '../../../../common/services'

import { isMenuScrapeable } from '../../../../menus/helpers'
import {
  useCanUserAssignTask,
  useCanUserReopenTask,
  useCanUserResolveTask,
  useCanUserUnassignTask,
} from '../../../hooks'

import MarkTaskAsAwaitingModal from './mark-task-as-awaiting-modal'

const { Group: ButtonGroup } = Button

const UNASSIGNED_PLACEHOLDER = 'Assign to me'

const TaskActionSection = ({
  task,
  taskType,
  permissions,
  onAssignTask,
  onUnassignTask,
  onSave,
  onDone,
  onDoneWithSecondaryOption,
  onMarkTaskAsAwaiting,
  onUnmarkTaskAsAwaiting,
  onReopenTask,
  cancelUrl,
  canReopenTask,
  menu,
  isSaveDisabled,
  secondaryOptionText,
  doneButtonTitle,
  buttonColors,
}) => {
  const {
    isAssigned,
    assignedTo,
    isAwaiting,
    status,
    isAssignable = true,
  } = task

  const { saveButtonColor, doneButtonColor } = buttonColors || {}
  const history = useHistory()
  const [isMarkAwaitingModalOpen, setIsMarkAwaitingModalOpen] = useState(false)

  const canUserAssignTask = useCanUserAssignTask(task, taskType, permissions)
  const canUserUnassignTask = useCanUserUnassignTask(permissions.UNASSIGN)

  const hasPermissionToReopenTask = useCanUserReopenTask(status, [
    permissions.REOPEN,
  ])

  const canUserReopenTask = useMemo(
    () => canReopenTask ?? hasPermissionToReopenTask,
    [canReopenTask, hasPermissionToReopenTask],
  )

  const isAttachedToCluster = useMemo(() => !!menu?.menuScrapingClusterId, [
    menu,
  ])

  const canResolveTask = useCanUserResolveTask(task, taskType, permissions)
  const isLoggedInUser = useIsLoggedInUser(task.assignedTo)

  const isAdmin = useUserHasRoles(ADMIN)
  const isTeamLeader = useUserHasRoles(TEAM_LEADER)

  useHotkeys(
    'command+shift+s, ctrl+shift+s',
    () => onSave && canResolveTask && onSave()[(onSave, canResolveTask)],
  )

  const assignedToCheckboxText = useMemo(
    () => (isAssigned ? assignedTo.name : UNASSIGNED_PLACEHOLDER),
    [isAssigned, assignedTo],
  )

  const onAssignedCheckboxChange = useCallback(
    ({ target: { checked } }) => (checked ? onAssignTask() : onUnassignTask()),
    [onAssignTask, onUnassignTask],
  )

  const handleMarkTaskAsAwaiting = useCallback(
    awaitingReasonData => {
      onMarkTaskAsAwaiting(awaitingReasonData)
      setIsMarkAwaitingModalOpen(false)
    },
    [onMarkTaskAsAwaiting],
  )

  const handleUnmarkTaskAsAwaiting = useCallback(() => {
    onUnmarkTaskAsAwaiting({ id: task.id })
  }, [onUnmarkTaskAsAwaiting, task.id])

  const doneButtonMargin = useMemo(
    () => (onDoneWithSecondaryOption ? 'no no no no' : 'no small no no'),
    [onDoneWithSecondaryOption],
  )

  const doneButtonMenu = useMemo(
    () => (
      <Menu onClick={onDoneWithSecondaryOption}>
        <Menu.Item key="1" disabled={!isMenuScrapeable(menu?.format)}>
          {secondaryOptionText}
        </Menu.Item>
      </Menu>
    ),
    [onDoneWithSecondaryOption, menu, secondaryOptionText],
  )

  const isSaveButtonDisabled =
    !canResolveTask || isAttachedToCluster || isSaveDisabled

  const isDoneButtonDisabled =
    !canResolveTask || (!!isAttachedToCluster && !isAdmin && !isTeamLeader)

  const isMarkAsAwaitingButtonDisabled =
    !isLoggedInUser && !isAdmin && !isTeamLeader

  const checkbox = (
    <Checkbox
      style={{ marginTop: '5px' }}
      checked={isAssigned}
      onChange={onAssignedCheckboxChange}
      disabled={
        isAssigned
          ? !canUserUnassignTask
          : !canUserAssignTask ||
            (!!isAttachedToCluster && !isAdmin && !isTeamLeader) ||
            !isAssignable
      }
    >
      {assignedToCheckboxText}
    </Checkbox>
  )

  return (
    <>
      {!isAssignable ? (
        <Tooltip
          title="Task is not ready for assignment in the workflow"
          trigger="hover"
        >
          {checkbox}
        </Tooltip>
      ) : (
        checkbox
      )}

      {onSave && (
        <Button
          onClick={onSave}
          margin="no small no no"
          type="primary"
          style={{
            width: '80px',
            ...(saveButtonColor &&
              !isSaveButtonDisabled && {
                backgroundColor: saveButtonColor,
                borderColor: saveButtonColor,
                color: THEME.colors.white,
              }),
          }}
          text={assignedToCheckboxText}
          disabled={isSaveButtonDisabled}
        >
          Save
        </Button>
      )}

      {onDone && (
        <ButtonGroup margin="no small no no">
          <Popconfirm
            title={doneButtonTitle}
            onConfirm={onDone}
            placement="bottom"
            okText="Yes"
            cancelText="No"
            disabled={isDoneButtonDisabled}
          >
            <Button
              margin={doneButtonMargin}
              type="primary"
              style={{
                width: '80px',
                ...(doneButtonColor &&
                  !isDoneButtonDisabled && {
                    backgroundColor: doneButtonColor,
                    borderColor: doneButtonColor,
                    color: THEME.colors.white,
                  }),
              }}
              disabled={isDoneButtonDisabled}
            >
              Done
            </Button>
          </Popconfirm>

          {onDoneWithSecondaryOption && (
            <Dropdown
              overlay={doneButtonMenu}
              placement="bottomLeft"
              disabled={isDoneButtonDisabled}
            >
              <Button margin="no small no no" type="primary">
                <Icon type="down" />
              </Button>
            </Dropdown>
          )}
        </ButtonGroup>
      )}

      {isAwaiting ? (
        <Button
          margin="no small no no"
          onClick={handleUnmarkTaskAsAwaiting}
          disabled={isMarkAsAwaitingButtonDisabled}
        >
          Unmark as awaiting
        </Button>
      ) : (
        <Button
          margin="no small no no"
          onClick={() => setIsMarkAwaitingModalOpen(true)}
          disabled={isMarkAsAwaitingButtonDisabled}
        >
          Mark as awaiting
        </Button>
      )}

      {onReopenTask && canUserReopenTask && (
        <Button margin="no small no no" onClick={onReopenTask}>
          Reopen
        </Button>
      )}

      <Button
        margin="no small no no"
        onClick={() => history.push(getCachedFilterForPage(cancelUrl))}
      >
        Go back
      </Button>

      <MarkTaskAsAwaitingModal
        markTaskAsAwaiting={handleMarkTaskAsAwaiting}
        isModalOpen={isMarkAwaitingModalOpen}
        closeModal={() => setIsMarkAwaitingModalOpen(false)}
      />
    </>
  )
}

TaskActionSection.propTypes = {
  task: PropTypes.object.isRequired,
  taskType: PropTypes.string.isRequired,
  permissions: PropTypes.object.isRequired,
  onAssignTask: PropTypes.func.isRequired,
  onUnassignTask: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  isSaveDisabled: PropTypes.bool,
  onDone: PropTypes.func,
  onDoneWithSecondaryOption: PropTypes.func,
  onMarkTaskAsAwaiting: PropTypes.func.isRequired,
  onUnmarkTaskAsAwaiting: PropTypes.func.isRequired,
  onReopenTask: PropTypes.func,
  cancelUrl: PropTypes.string.isRequired,
  canReopenTask: PropTypes.bool,
  menu: PropTypes.object,
  secondaryOptionText: PropTypes.string,
  doneButtonTitle: PropTypes.string,
  buttonColors: PropTypes.object,
}

TaskActionSection.defaultProps = {
  onSave: undefined,
  isSaveDisabled: false,
  onDone: undefined,
  onDoneWithSecondaryOption: undefined,
  onReopenTask: undefined,
  canReopenTask: undefined,
  menu: undefined,
  secondaryOptionText: 'Resolve with debug mode scraping',
  doneButtonTitle: 'Are you sure you want to resolve this task?',
  buttonColors: undefined,
}

export default TaskActionSection
